import { authApi, usersApi, ruhsatlarApi, policelerApi, tekliflerApi, muayenelerApi, belgelerApi, iptallerApi, adminStatsApi, API_URL } from './utils/api';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function validateFiles(input: HTMLInputElement): string | null {
    const files = input.files;
    if (!files || files.length === 0) return null;
    for (let i = 0; i < files.length; i++) {
        if (files[i].size > MAX_FILE_SIZE) {
            return `"${files[i].name}" dosyası çok büyük (${(files[i].size / 1024 / 1024).toFixed(1)}MB). Maksimum 10MB yüklenebilir.`;
        }
    }
    return null;
}

function attachFileValidation() {
    document.querySelectorAll('input[type="file"]').forEach(input => {
        input.addEventListener('change', () => {
            const err = validateFiles(input as HTMLInputElement);
            if (err) {
                alert(err);
                (input as HTMLInputElement).value = '';
            }
        });
    });
}

export class Router {
    private routes: Map<string, () => string> = new Map();
    private container: HTMLElement | null = null;

    constructor(containerId: string) {
        this.container = document.getElementById(containerId);
        window.addEventListener('popstate', () => this.handleRoute());
    }

    addRoute(path: string, handler: () => string) {
        this.routes.set(path, handler);
    }

    navigate(path: string) {
        window.history.pushState({}, '', path);
        this.handleRoute();
    }

    handleRoute() {
        const path = window.location.pathname;
        const handler = this.routes.get(path) || this.routes.get('/');

        if (handler && this.container) {
            this.container.innerHTML = handler();
            this.attachEventListeners();
            attachFileValidation();
            this.loadPageData();
        }
    }

    private attachEventListeners() {
        // FAQ functionality
        const faqItems = document.querySelectorAll('.faq-item');
        for (const item of faqItems) {
            const question = item.querySelector('.faq-question');
            question?.addEventListener('click', () => {
                const isActive = item.classList.contains('active');
                for (const otherItem of faqItems) {
                    otherItem.classList.remove('active');
                }
                if (!isActive) {
                    item.classList.add('active');
                }
            });
        }

        // Contact form
        const contactForm = document.querySelector('.contact-form');
        contactForm?.addEventListener('submit', (e) => {
            e.preventDefault();
            alert('Form başarıyla gönderildi!');
        });

        // Quote form
        const quoteForm = document.querySelector('.quote-form') as HTMLFormElement;
        quoteForm?.addEventListener('submit', (e) => {
            e.preventDefault();
            const insuranceType = quoteForm.getAttribute('data-insurance-type') || 'bilinmiyor';
            const tcNo = (document.getElementById('tcNo') as HTMLInputElement)?.value || '';
            const birthDate = (document.getElementById('birthDate') as HTMLInputElement)?.value || '';
            const phone = (document.getElementById('phone') as HTMLInputElement)?.value || '';
            const timestamp = new Date().toLocaleString('tr-TR');
            const csvRow = `"${timestamp}","${insuranceType}","${tcNo}","${birthDate}","${phone}"`;
            const csvHeader = '"Tarih","Sigorta Tipi","TC/Vergi No","Doğum Tarihi","Telefon"';
            let existingData = localStorage.getItem('insuranceQuotes');
            if (!existingData) existingData = csvHeader;
            const newData = existingData + '\n' + csvRow;
            localStorage.setItem('insuranceQuotes', newData);
            const blob = new Blob([newData], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `teklif_talepleri_${Date.now()}.csv`;
            link.click();
            URL.revokeObjectURL(link.href);
            alert('Teklif talebiniz alındı! CSV dosyası indirildi.');
            quoteForm.reset();
        });

        // Login form
        const loginForm = document.getElementById('loginForm');
        loginForm?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const email = (document.getElementById('email') as HTMLInputElement)?.value;
            const sifre = (document.getElementById('sifre') as HTMLInputElement)?.value;
            const errorDiv = document.getElementById('loginError');

            try {
                await authApi.login(email, sifre);
                this.updateAuthUI();
                this.navigate('/hesabim');
            } catch (error: any) {
                if (errorDiv) {
                    errorDiv.textContent = error.message;
                    errorDiv.style.display = 'block';
                }
            }
        });

        // Logout button
        const logoutBtn = document.getElementById('logoutBtn');
        logoutBtn?.addEventListener('click', (e) => {
            e.preventDefault();
            authApi.logout();
            this.updateAuthUI();
            this.navigate('/');
        });

        // Teklif form
        this.attachTeklifFormListener();
        // Admin: Users
        this.attachUserFormListeners();
        // Admin: Ruhsatlar
        this.attachRuhsatFormListeners();
        // Admin: Policeler
        this.attachPoliceFormListeners();
        // Admin: Teklifler
        this.attachTekliflerListeners();
        // Admin: Muayeneler
        this.attachMuayeneListeners();
        // Admin: Belgeler
        this.attachBelgeListeners();
        // Admin: İptaller
        this.attachIptalListeners();
    }

    private attachUserFormListeners() {
        const addUserBtn = document.getElementById('addUserBtn');
        const userModal = document.getElementById('userModal');
        const closeModal = document.getElementById('closeModal');
        const cancelBtn = document.getElementById('cancelBtn');
        const userForm = document.getElementById('userForm');

        const openModal = (isEdit = false) => {
            if (userModal) {
                userModal.style.display = 'flex';
                const title = document.getElementById('modalTitle');
                const sifreNote = document.getElementById('sifreNote');
                if (title) title.textContent = isEdit ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Ekle';
                if (sifreNote) sifreNote.textContent = isEdit ? '(boş bırakılırsa değişmez)' : '(zorunlu)';
            }
        };

        const closeModalFn = () => {
            if (userModal) userModal.style.display = 'none';
            (userForm as HTMLFormElement)?.reset();
            (document.getElementById('userId') as HTMLInputElement).value = '';
        };

        addUserBtn?.addEventListener('click', () => openModal(false));
        closeModal?.addEventListener('click', closeModalFn);
        cancelBtn?.addEventListener('click', closeModalFn);

        userForm?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const userId = (document.getElementById('userId') as HTMLInputElement)?.value;
            const userData = {
                ad: (document.getElementById('ad') as HTMLInputElement)?.value,
                soyad: (document.getElementById('soyad') as HTMLInputElement)?.value,
                email: (document.getElementById('userEmail') as HTMLInputElement)?.value,
                telefon: (document.getElementById('telefon') as HTMLInputElement)?.value,
                sifre: (document.getElementById('userSifre') as HTMLInputElement)?.value,
                rol: (document.getElementById('rol') as HTMLSelectElement)?.value
            };

            const errorDiv = document.getElementById('userFormError');
            try {
                if (userId) {
                    await usersApi.update(parseInt(userId), userData);
                } else {
                    await usersApi.create(userData);
                }
                closeModalFn();
                this.loadUsers();
            } catch (error: any) {
                if (errorDiv) {
                    errorDiv.textContent = error.message;
                    errorDiv.style.display = 'block';
                }
            }
        });

        // Edit/Delete buttons (event delegation)
        document.getElementById('usersList')?.addEventListener('click', async (e) => {
            const target = e.target as HTMLElement;
            const btn = target.closest('button');
            if (!btn) return;

            const userId = btn.getAttribute('data-id');
            if (!userId) return;

            if (btn.classList.contains('edit-btn')) {
                const user = await usersApi.getById(parseInt(userId));
                (document.getElementById('userId') as HTMLInputElement).value = user.id;
                (document.getElementById('ad') as HTMLInputElement).value = user.ad;
                (document.getElementById('soyad') as HTMLInputElement).value = user.soyad;
                (document.getElementById('userEmail') as HTMLInputElement).value = user.email;
                (document.getElementById('telefon') as HTMLInputElement).value = user.telefon || '';
                (document.getElementById('rol') as HTMLSelectElement).value = user.rol;
                (document.getElementById('userSifre') as HTMLInputElement).value = '';
                openModal(true);
            } else if (btn.classList.contains('delete-btn')) {
                if (confirm('Bu kullanıcıyı silmek istediğinize emin misiniz?')) {
                    await usersApi.delete(parseInt(userId));
                    this.loadUsers();
                }
            } else if (btn.classList.contains('add-ruhsat-btn')) {
                // Hızlı Ruhsat Ekle
                const userName = btn.getAttribute('data-name') || '';
                const qrModal = document.getElementById('quickRuhsatModal');
                const qrTitle = document.getElementById('quickRuhsatTitle');
                (document.getElementById('qrUserId') as HTMLInputElement).value = userId;
                if (qrTitle) qrTitle.textContent = `${userName} — Ruhsat Ekle`;
                if (qrModal) qrModal.style.display = 'flex';
            } else if (btn.classList.contains('add-police-btn')) {
                // Hızlı Poliçe Ekle
                const userName = btn.getAttribute('data-name') || '';
                const qpModal = document.getElementById('quickPoliceModal');
                const qpTitle = document.getElementById('quickPoliceTitle');
                (document.getElementById('qpUserId') as HTMLInputElement).value = userId;
                if (qpTitle) qpTitle.textContent = `${userName} — Poliçe Ekle`;
                // Load user's ruhsatlar
                try {
                    const ruhsatlar = await ruhsatlarApi.getAll();
                    const userRuhsatlar = ruhsatlar.filter((r: any) => r.user_id === parseInt(userId));
                    const ruhsatSelect = document.getElementById('qpRuhsatId') as HTMLSelectElement;
                    if (ruhsatSelect) ruhsatSelect.innerHTML = '<option value="">Ruhsat Seçin (Opsiyonel)</option>' +
                        userRuhsatlar.map((r: any) => `<option value="${r.id}">${r.plaka || 'Plakasız'} - ${r.marka || ''} ${r.model || ''}</option>`).join('');
                } catch(e) {}
                if (qpModal) qpModal.style.display = 'flex';
            } else if (btn.classList.contains('add-belge-btn')) {
                // Hızlı Belge Ekle
                const userName = btn.getAttribute('data-name') || '';
                const qbModal = document.getElementById('quickBelgeModal');
                const qbTitle = document.getElementById('quickBelgeTitle');
                (document.getElementById('qbUserId') as HTMLInputElement).value = userId;
                if (qbTitle) qbTitle.textContent = `${userName} — Belge Ekle`;
                if (qbModal) qbModal.style.display = 'flex';
            }
        });

        // Şifre Üret butonu
        document.getElementById('generatePassword')?.addEventListener('click', () => {
            const chars = '0123456789';
            let pass = '';
            for (let i = 0; i < 8; i++) pass += chars.charAt(Math.floor(Math.random() * chars.length));
            const sifreInput = document.getElementById('userSifre') as HTMLInputElement;
            if (sifreInput) { sifreInput.type = 'text'; sifreInput.value = pass; }
        });

        // Hızlı Ruhsat modal kapatma + submit
        document.getElementById('closeQuickRuhsat')?.addEventListener('click', () => {
            const m = document.getElementById('quickRuhsatModal'); if (m) m.style.display = 'none';
            (document.getElementById('quickRuhsatForm') as HTMLFormElement)?.reset();
        });
        document.getElementById('cancelQuickRuhsat')?.addEventListener('click', () => {
            const m = document.getElementById('quickRuhsatModal'); if (m) m.style.display = 'none';
            (document.getElementById('quickRuhsatForm') as HTMLFormElement)?.reset();
        });
        document.getElementById('quickRuhsatForm')?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const userId = (document.getElementById('qrUserId') as HTMLInputElement)?.value;
            const formData = new FormData();
            formData.append('user_id', userId);
            formData.append('plaka', (document.getElementById('qrPlaka') as HTMLInputElement)?.value || '');
            formData.append('ruhsat_sahibi', (document.getElementById('qrSahibi') as HTMLInputElement)?.value || '');
            formData.append('marka', (document.getElementById('qrMarka') as HTMLInputElement)?.value || '');
            formData.append('model', (document.getElementById('qrModel') as HTMLInputElement)?.value || '');
            formData.append('model_yili', (document.getElementById('qrModelYili') as HTMLInputElement)?.value || '');
            formData.append('sasi_no', (document.getElementById('qrSasiNo') as HTMLInputElement)?.value || '');
            formData.append('motor_no', (document.getElementById('qrMotorNo') as HTMLInputElement)?.value || '');
            formData.append('tc_no', (document.getElementById('qrTcNo') as HTMLInputElement)?.value || '');
            const imageFile = (document.getElementById('qrImage') as HTMLInputElement)?.files?.[0];
            if (imageFile) formData.append('ruhsat_image', imageFile);
            const errDiv = document.getElementById('qrError');
            try {
                await ruhsatlarApi.create(formData);
                const m = document.getElementById('quickRuhsatModal'); if (m) m.style.display = 'none';
                (document.getElementById('quickRuhsatForm') as HTMLFormElement)?.reset();
                alert('Ruhsat eklendi!');
            } catch (error: any) {
                if (errDiv) { errDiv.textContent = error.message; errDiv.style.display = 'block'; }
            }
        });

        // Hızlı Poliçe modal kapatma + submit
        document.getElementById('closeQuickPolice')?.addEventListener('click', () => {
            const m = document.getElementById('quickPoliceModal'); if (m) m.style.display = 'none';
            (document.getElementById('quickPoliceForm') as HTMLFormElement)?.reset();
        });
        document.getElementById('cancelQuickPolice')?.addEventListener('click', () => {
            const m = document.getElementById('quickPoliceModal'); if (m) m.style.display = 'none';
            (document.getElementById('quickPoliceForm') as HTMLFormElement)?.reset();
        });
        document.getElementById('quickPoliceForm')?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const userId = (document.getElementById('qpUserId') as HTMLInputElement)?.value;
            const policeData = {
                user_id: userId,
                ruhsat_id: (document.getElementById('qpRuhsatId') as HTMLSelectElement)?.value || null,
                sigorta_tipi: (document.getElementById('qpSigortaTipi') as HTMLSelectElement)?.value,
                police_no: (document.getElementById('qpPoliceNo') as HTMLInputElement)?.value,
                baslangic_tarihi: (document.getElementById('qpBaslangic') as HTMLInputElement)?.value,
                bitis_tarihi: (document.getElementById('qpBitis') as HTMLInputElement)?.value,
                prim_tutari: (document.getElementById('qpPrim') as HTMLInputElement)?.value,
                durum: (document.getElementById('qpDurum') as HTMLSelectElement)?.value || 'aktif',
                muayene_tarihi: (document.getElementById('qpMuayene') as HTMLInputElement)?.value,
                notlar: (document.getElementById('qpNotlar') as HTMLTextAreaElement)?.value
            };
            const errDiv = document.getElementById('qpError');
            try {
                const savedPolice = await policelerApi.create(policeData);
                // Belge yükleme
                const files = (document.getElementById('qpBelge') as HTMLInputElement)?.files;
                if (files && files.length > 0 && savedPolice?.police?.id) {
                    for (let i = 0; i < files.length; i++) {
                        const fd = new FormData();
                        fd.append('dosya', files[i]);
                        fd.append('police_id', savedPolice.police.id.toString());
                        fd.append('user_id', userId);
                        fd.append('belge_tipi', files[i].type === 'application/pdf' ? 'police_pdf' : 'evrak');
                        try { await belgelerApi.create(fd); } catch(e) {}
                    }
                }
                const m = document.getElementById('quickPoliceModal'); if (m) m.style.display = 'none';
                (document.getElementById('quickPoliceForm') as HTMLFormElement)?.reset();
                alert('Poliçe eklendi!');
            } catch (error: any) {
                if (errDiv) { errDiv.textContent = error.message; errDiv.style.display = 'block'; }
            }
        });

        // Hızlı Belge modal kapatma + submit
        document.getElementById('closeQuickBelge')?.addEventListener('click', () => {
            const m = document.getElementById('quickBelgeModal'); if (m) m.style.display = 'none';
            (document.getElementById('quickBelgeForm') as HTMLFormElement)?.reset();
        });
        document.getElementById('cancelQuickBelge')?.addEventListener('click', () => {
            const m = document.getElementById('quickBelgeModal'); if (m) m.style.display = 'none';
            (document.getElementById('quickBelgeForm') as HTMLFormElement)?.reset();
        });
        document.getElementById('quickBelgeForm')?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const userId = (document.getElementById('qbUserId') as HTMLInputElement)?.value;
            const files = (document.getElementById('qbDosya') as HTMLInputElement)?.files;
            const belgeTipi = (document.getElementById('qbBelgeTipi') as HTMLSelectElement)?.value;
            const aciklama = (document.getElementById('qbAciklama') as HTMLInputElement)?.value;
            const errDiv = document.getElementById('qbError');
            if (!files || files.length === 0) return;
            try {
                for (let i = 0; i < files.length; i++) {
                    const fd = new FormData();
                    fd.append('dosya', files[i]);
                    fd.append('user_id', userId);
                    fd.append('belge_tipi', belgeTipi);
                    fd.append('aciklama', aciklama || files[i].name);
                    await belgelerApi.create(fd);
                }
                const m = document.getElementById('quickBelgeModal'); if (m) m.style.display = 'none';
                (document.getElementById('quickBelgeForm') as HTMLFormElement)?.reset();
                alert(`${files.length} belge yüklendi!`);
            } catch (error: any) {
                if (errDiv) { errDiv.textContent = error.message; errDiv.style.display = 'block'; }
            }
        });
    }

    private attachRuhsatFormListeners() {
        const addBtn = document.getElementById('addRuhsatBtn');
        const modal = document.getElementById('ruhsatModal');
        const closeBtn = document.getElementById('closeRuhsatModal');
        const cancelBtn = document.getElementById('cancelRuhsatBtn');
        const form = document.getElementById('ruhsatForm');
        const imageInput = document.getElementById('ruhsatImage') as HTMLInputElement;

        const openModal = (isEdit = false) => {
            if (modal) {
                modal.style.display = 'flex';
                const title = document.getElementById('ruhsatModalTitle');
                if (title) title.textContent = isEdit ? 'Ruhsat Düzenle' : 'Yeni Ruhsat Ekle';
            }
        };

        const closeModal = () => {
            if (modal) modal.style.display = 'none';
            (form as HTMLFormElement)?.reset();
            (document.getElementById('ruhsatId') as HTMLInputElement).value = '';
            const preview = document.getElementById('imagePreview');
            if (preview) preview.innerHTML = '';
        };

        addBtn?.addEventListener('click', () => openModal(false));
        closeBtn?.addEventListener('click', closeModal);
        cancelBtn?.addEventListener('click', closeModal);

        imageInput?.addEventListener('change', () => {
            const file = imageInput.files?.[0];
            const preview = document.getElementById('imagePreview');
            if (file && preview) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    preview.innerHTML = `<img src="${e.target?.result}" alt="Preview">`;
                };
                reader.readAsDataURL(file);
            }
        });

        form?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const ruhsatId = (document.getElementById('ruhsatId') as HTMLInputElement)?.value;
            const formData = new FormData();
            formData.append('user_id', (document.getElementById('ruhsatUserId') as HTMLSelectElement)?.value);
            formData.append('plaka', (document.getElementById('plaka') as HTMLInputElement)?.value);
            formData.append('ruhsat_sahibi', (document.getElementById('ruhsatSahibi') as HTMLInputElement)?.value);
            formData.append('marka', (document.getElementById('marka') as HTMLInputElement)?.value);
            formData.append('model', (document.getElementById('model') as HTMLInputElement)?.value);
            formData.append('model_yili', (document.getElementById('modelYili') as HTMLInputElement)?.value);
            formData.append('sasi_no', (document.getElementById('sasiNo') as HTMLInputElement)?.value);
            formData.append('motor_no', (document.getElementById('motorNo') as HTMLInputElement)?.value);
            formData.append('tc_no', (document.getElementById('tcNo') as HTMLInputElement)?.value);

            const imageFile = (document.getElementById('ruhsatImage') as HTMLInputElement)?.files?.[0];
            if (imageFile) formData.append('ruhsat_image', imageFile);

            const errorDiv = document.getElementById('ruhsatFormError');
            try {
                if (ruhsatId) {
                    await ruhsatlarApi.update(parseInt(ruhsatId), formData);
                } else {
                    await ruhsatlarApi.create(formData);
                }
                closeModal();
                this.loadRuhsatlar();
            } catch (error: any) {
                if (errorDiv) {
                    errorDiv.textContent = error.message;
                    errorDiv.style.display = 'block';
                }
            }
        });

        document.getElementById('ruhsatlarList')?.addEventListener('click', async (e) => {
            const target = e.target as HTMLElement;
            const btn = target.closest('button');
            if (!btn) return;

            const id = btn.getAttribute('data-id');
            if (!id) return;

            if (btn.classList.contains('delete-btn')) {
                if (confirm('Bu ruhsatı silmek istediğinize emin misiniz?')) {
                    await ruhsatlarApi.delete(parseInt(id));
                    this.loadRuhsatlar();
                }
            }
        });
    }

    private attachPoliceFormListeners() {
        const addBtn = document.getElementById('addPoliceBtn');
        const modal = document.getElementById('policeModal');
        const closeBtn = document.getElementById('closePoliceModal');
        const cancelBtn = document.getElementById('cancelPoliceBtn');
        const form = document.getElementById('policeForm');

        const openModal = (isEdit = false) => {
            if (modal) {
                modal.style.display = 'flex';
                const title = document.getElementById('policeModalTitle');
                if (title) title.textContent = isEdit ? 'Poliçe Düzenle' : 'Yeni Poliçe Ekle';
            }
        };

        const closeModal = () => {
            if (modal) modal.style.display = 'none';
            (form as HTMLFormElement)?.reset();
            (document.getElementById('policeId') as HTMLInputElement).value = '';
        };

        addBtn?.addEventListener('click', () => openModal(false));
        closeBtn?.addEventListener('click', closeModal);
        cancelBtn?.addEventListener('click', closeModal);

        // Load ruhsatlar when user changes
        document.getElementById('policeUserId')?.addEventListener('change', async (e) => {
            const userId = (e.target as HTMLSelectElement).value;
            const ruhsatSelect = document.getElementById('policeRuhsatId') as HTMLSelectElement;
            if (ruhsatSelect && userId) {
                const ruhsatlar = await ruhsatlarApi.getAll();
                const userRuhsatlar = ruhsatlar.filter((r: any) => r.user_id === parseInt(userId));
                ruhsatSelect.innerHTML = '<option value="">Ruhsat Seçin (Opsiyonel)</option>' +
                    userRuhsatlar.map((r: any) => `<option value="${r.id}">${r.plaka || 'Plakasız'} - ${r.marka || ''} ${r.model || ''}</option>`).join('');
            }
        });

        form?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const policeId = (document.getElementById('policeId') as HTMLInputElement)?.value;
            const policeData = {
                user_id: (document.getElementById('policeUserId') as HTMLSelectElement)?.value,
                ruhsat_id: (document.getElementById('policeRuhsatId') as HTMLSelectElement)?.value || null,
                sigorta_tipi: (document.getElementById('sigortaTipi') as HTMLSelectElement)?.value,
                police_no: (document.getElementById('policeNo') as HTMLInputElement)?.value,
                baslangic_tarihi: (document.getElementById('baslangicTarihi') as HTMLInputElement)?.value,
                bitis_tarihi: (document.getElementById('bitisTarihi') as HTMLInputElement)?.value,
                prim_tutari: (document.getElementById('primTutari') as HTMLInputElement)?.value,
                durum: (document.getElementById('policeDurum') as HTMLSelectElement)?.value,
                muayene_tarihi: (document.getElementById('muayeneTarihi') as HTMLInputElement)?.value,
                notlar: (document.getElementById('notlar') as HTMLTextAreaElement)?.value
            };

            const errorDiv = document.getElementById('policeFormError');
            const belgeInput = document.getElementById('policeBelge') as HTMLInputElement;
            try {
                let savedPolice: any;
                if (policeId) {
                    savedPolice = await policelerApi.update(parseInt(policeId), policeData);
                } else {
                    savedPolice = await policelerApi.create(policeData);
                }

                // Belge yükleme (varsa)
                const files = belgeInput?.files;
                if (files && files.length > 0 && savedPolice?.police?.id) {
                    for (let i = 0; i < files.length; i++) {
                        const formData = new FormData();
                        formData.append('dosya', files[i]);
                        formData.append('police_id', savedPolice.police.id.toString());
                        formData.append('user_id', policeData.user_id);
                        formData.append('belge_tipi', files[i].type === 'application/pdf' ? 'police_pdf' : 'evrak');
                        formData.append('aciklama', `${policeData.sigorta_tipi} poliçesi belgesi`);
                        try { await belgelerApi.create(formData); } catch(e) { console.error('Belge yükleme hatası:', e); }
                    }
                }

                closeModal();
                this.loadPoliceler();
            } catch (error: any) {
                if (errorDiv) {
                    errorDiv.textContent = error.message;
                    errorDiv.style.display = 'block';
                }
            }
        });

        document.getElementById('policelerList')?.addEventListener('click', async (e) => {
            const target = e.target as HTMLElement;
            const btn = target.closest('button');
            if (!btn) return;

            const id = btn.getAttribute('data-id');
            if (!id) return;

            if (btn.classList.contains('delete-btn')) {
                if (confirm('Bu poliçeyi silmek istediğinize emin misiniz?')) {
                    await policelerApi.delete(parseInt(id));
                    this.loadPoliceler();
                }
            }
        });

        // Filters
        document.getElementById('filterTip')?.addEventListener('change', () => this.loadPoliceler());
        document.getElementById('filterDurum')?.addEventListener('change', () => this.loadPoliceler());
    }

    private attachTeklifFormListener() {
        const form = document.getElementById('teklifForm');
        const sigortaTipiSelect = document.getElementById('teklifSigortaTipi') as HTMLSelectElement;
        const aracAlanlari = document.getElementById('aracAlanlari');
        const plakaInput = document.getElementById('teklifPlaka') as HTMLInputElement;
        const belgeSeriInput = document.getElementById('teklifBelgeSeri') as HTMLInputElement;
        const aracTipleri = ['trafik', 'kasko', 'imm', 'e-kasko'];

        // Sigorta tipi değişince araç alanlarını göster/gizle
        sigortaTipiSelect?.addEventListener('change', () => {
            const isArac = aracTipleri.includes(sigortaTipiSelect.value);
            if (aracAlanlari) aracAlanlari.style.display = isArac ? 'grid' : 'none';
            if (plakaInput) plakaInput.required = isArac;
            if (belgeSeriInput) belgeSeriInput.required = isArac;
            // Araç değilse alanları temizle
            if (!isArac) {
                if (plakaInput) plakaInput.value = '';
                if (belgeSeriInput) belgeSeriInput.value = '';
            }
        });

        form?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const errorDiv = document.getElementById('teklifError');
            const successDiv = document.getElementById('teklifSuccess');
            const selectedTip = sigortaTipiSelect?.value;
            const isArac = aracTipleri.includes(selectedTip);

            const teklifData: any = {
                unvan: (document.getElementById('teklifUnvan') as HTMLInputElement)?.value,
                tc_vergi_no: (document.getElementById('teklifTcVergi') as HTMLInputElement)?.value || null,
                telefon: (document.getElementById('teklifTelefon') as HTMLInputElement)?.value,
                eposta: (document.getElementById('teklifEposta') as HTMLInputElement)?.value,
                sigorta_tipi: selectedTip,
                mesaj: (document.getElementById('teklifMesaj') as HTMLTextAreaElement)?.value || null
            };

            // Araç alanlarını sadece araç sigortası ise ekle
            if (isArac) {
                teklifData.plaka = plakaInput?.value;
                teklifData.belge_seri_no = belgeSeriInput?.value;
            }

            try {
                const result = await tekliflerApi.create(teklifData);
                if (successDiv) {
                    successDiv.textContent = result.message || 'Teklif talebiniz alınmıştır!';
                    successDiv.style.display = 'block';
                }
                if (errorDiv) errorDiv.style.display = 'none';
                (form as HTMLFormElement).reset();
                if (aracAlanlari) aracAlanlari.style.display = 'none';
            } catch (error: any) {
                if (errorDiv) {
                    errorDiv.textContent = error.message;
                    errorDiv.style.display = 'block';
                }
                if (successDiv) successDiv.style.display = 'none';
            }
        });
    }

    private attachTekliflerListeners() {
        const modal = document.getElementById('teklifModal');
        const closeBtn = document.getElementById('closeTeklifModal');
        const cancelBtn = document.getElementById('cancelTeklifBtn');
        const form = document.getElementById('teklifUpdateForm');

        const closeModal = () => {
            if (modal) modal.style.display = 'none';
        };

        closeBtn?.addEventListener('click', closeModal);
        cancelBtn?.addEventListener('click', closeModal);

        form?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const teklifId = (document.getElementById('teklifId') as HTMLInputElement)?.value;
            const durum = (document.getElementById('teklifDurum') as HTMLSelectElement)?.value;
            const admin_notu = (document.getElementById('teklifAdminNotu') as HTMLTextAreaElement)?.value;

            try {
                await tekliflerApi.update(parseInt(teklifId), { durum, admin_notu });
                closeModal();
                this.loadTeklifler();
            } catch (error: any) {
                alert(error.message);
            }
        });

        // Event delegation for view/delete buttons
        document.getElementById('tekliflerList')?.addEventListener('click', async (e) => {
            const target = e.target as HTMLElement;
            const btn = target.closest('button');
            if (!btn) return;

            const id = btn.getAttribute('data-id');
            if (!id) return;

            if (btn.classList.contains('view-btn')) {
                const teklif = await tekliflerApi.getById(parseInt(id));
                const detail = document.getElementById('teklifDetail');
                if (detail) {
                    detail.innerHTML = `
                        <div class="teklif-detail-grid">
                            <p><strong>Ünvan:</strong> ${teklif.unvan}</p>
                            <p><strong>TC/Vergi No:</strong> ${teklif.tc_vergi_no || '-'}</p>
                            <p><strong>Telefon:</strong> ${teklif.telefon}</p>
                            <p><strong>E-posta:</strong> ${teklif.eposta}</p>
                            <p><strong>Plaka:</strong> ${teklif.plaka}</p>
                            <p><strong>Belge Seri No:</strong> ${teklif.belge_seri_no}</p>
                            <p><strong>Sigorta Tipi:</strong> ${teklif.sigorta_tipi}</p>
                            <p><strong>Mesaj:</strong> ${teklif.mesaj || '-'}</p>
                            <p><strong>Tarih:</strong> ${teklif.created_at}</p>
                        </div>
                    `;
                }
                (document.getElementById('teklifId') as HTMLInputElement).value = teklif.id;
                (document.getElementById('teklifDurum') as HTMLSelectElement).value = teklif.durum;
                (document.getElementById('teklifAdminNotu') as HTMLTextAreaElement).value = teklif.admin_notu || '';
                if (modal) modal.style.display = 'flex';
            } else if (btn.classList.contains('delete-btn')) {
                if (confirm('Bu teklifi silmek istediğinize emin misiniz?')) {
                    await tekliflerApi.delete(parseInt(id));
                    this.loadTeklifler();
                }
            }
        });

        // Filter
        document.getElementById('filterTeklifDurum')?.addEventListener('change', () => this.loadTeklifler());
    }

    private async loadTeklifler() {
        const container = document.getElementById('tekliflerList');
        if (!container) return;

        try {
            let teklifler = await tekliflerApi.getAll();

            const filterDurum = (document.getElementById('filterTeklifDurum') as HTMLSelectElement)?.value;
            if (filterDurum) teklifler = teklifler.filter((t: any) => t.durum === filterDurum);

            container.innerHTML = `
                <table class="data-table">
                    <thead>
                        <tr>
                            <th>Tarih</th>
                            <th>Ünvan</th>
                            <th>Telefon</th>
                            <th>Plaka</th>
                            <th>Sigorta Tipi</th>
                            <th>Durum</th>
                            <th>İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${teklifler.map((t: any) => `
                            <tr>
                                <td>${t.created_at ? new Date(t.created_at).toLocaleDateString('tr-TR') : '-'}</td>
                                <td>${t.unvan}</td>
                                <td>${t.telefon}</td>
                                <td><strong>${t.plaka}</strong></td>
                                <td><span class="badge">${t.sigorta_tipi}</span></td>
                                <td><span class="badge badge-${t.durum}">${t.durum}</span></td>
                                <td class="actions">
                                    <button class="btn-sm view-btn" data-id="${t.id}">Detay</button>
                                    <button class="btn-sm btn-danger delete-btn" data-id="${t.id}">Sil</button>
                                </td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
                ${teklifler.length === 0 ? '<p class="empty-state">Henüz teklif talebi yok.</p>' : ''}
            `;
        } catch (error) {
            container.innerHTML = '<p class="error">Teklifler yüklenirken hata oluştu.</p>';
        }
    }

    private attachMuayeneListeners() {
        const modal = document.getElementById('muayeneModal');
        const addBtn = document.getElementById('addMuayeneBtn');
        const closeBtn = document.getElementById('closeMuayeneModal');
        const cancelBtn = document.getElementById('cancelMuayeneBtn');
        const form = document.getElementById('muayeneForm');

        const closeModal = () => { if (modal) modal.style.display = 'none'; (form as HTMLFormElement)?.reset(); };
        addBtn?.addEventListener('click', () => { if (modal) modal.style.display = 'flex'; });
        closeBtn?.addEventListener('click', closeModal);
        cancelBtn?.addEventListener('click', closeModal);

        form?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const data = {
                ruhsat_id: (document.getElementById('muayeneRuhsatId') as HTMLSelectElement)?.value,
                muayene_tarihi: (document.getElementById('muayeneTarihi') as HTMLInputElement)?.value,
                sonraki_muayene: (document.getElementById('sonrakiMuayene') as HTMLInputElement)?.value,
                sonuc: (document.getElementById('muayeneSonuc') as HTMLSelectElement)?.value,
                notlar: (document.getElementById('muayeneNotlar') as HTMLTextAreaElement)?.value
            };
            const errorDiv = document.getElementById('muayeneFormError');
            try {
                await muayenelerApi.create(data);
                closeModal();
                this.loadMuayeneler();
            } catch (error: any) {
                if (errorDiv) { errorDiv.textContent = error.message; errorDiv.style.display = 'block'; }
            }
        });

        document.getElementById('muayenelerList')?.addEventListener('click', async (e) => {
            const btn = (e.target as HTMLElement).closest('button');
            if (!btn) return;
            const id = btn.getAttribute('data-id');
            if (!id) return;
            if (btn.classList.contains('delete-btn')) {
                if (confirm('Bu muayene kaydını silmek istediğinize emin misiniz?')) {
                    await muayenelerApi.delete(parseInt(id));
                    this.loadMuayeneler();
                }
            }
        });
    }

    private attachBelgeListeners() {
        const modal = document.getElementById('belgeModal');
        const addBtn = document.getElementById('addBelgeBtn');
        const closeBtn = document.getElementById('closeBelgeModal');
        const cancelBtn = document.getElementById('cancelBelgeBtn');
        const form = document.getElementById('belgeForm');

        const closeModal = () => { if (modal) modal.style.display = 'none'; (form as HTMLFormElement)?.reset(); };
        addBtn?.addEventListener('click', () => { if (modal) modal.style.display = 'flex'; });
        closeBtn?.addEventListener('click', closeModal);
        cancelBtn?.addEventListener('click', closeModal);

        form?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const formData = new FormData();
            formData.append('belge_tipi', (document.getElementById('belgeTipi') as HTMLSelectElement)?.value);
            const userId = (document.getElementById('belgeUserId') as HTMLSelectElement)?.value;
            const policeId = (document.getElementById('belgePoliceId') as HTMLSelectElement)?.value;
            if (userId) formData.append('user_id', userId);
            if (policeId) formData.append('police_id', policeId);
            const file = (document.getElementById('belgeDosya') as HTMLInputElement)?.files?.[0];
            if (file) formData.append('dosya', file);
            const aciklama = (document.getElementById('belgeAciklama') as HTMLInputElement)?.value;
            if (aciklama) formData.append('aciklama', aciklama);

            const errorDiv = document.getElementById('belgeFormError');
            try {
                await belgelerApi.create(formData);
                closeModal();
                this.loadBelgeler();
            } catch (error: any) {
                if (errorDiv) { errorDiv.textContent = error.message; errorDiv.style.display = 'block'; }
            }
        });

        document.getElementById('belgelerList')?.addEventListener('click', async (e) => {
            const btn = (e.target as HTMLElement).closest('button');
            if (!btn) return;
            const id = btn.getAttribute('data-id');
            if (!id) return;
            if (btn.classList.contains('delete-btn')) {
                if (confirm('Bu belgeyi silmek istediğinize emin misiniz?')) {
                    await belgelerApi.delete(parseInt(id));
                    this.loadBelgeler();
                }
            }
        });
    }

    private attachIptalListeners() {
        const modal = document.getElementById('iptalModal');
        const addBtn = document.getElementById('addIptalBtn');
        const closeBtn = document.getElementById('closeIptalModal');
        const cancelBtn = document.getElementById('cancelIptalBtn');
        const form = document.getElementById('iptalForm');

        const closeModal = () => { if (modal) modal.style.display = 'none'; (form as HTMLFormElement)?.reset(); };
        addBtn?.addEventListener('click', () => { if (modal) modal.style.display = 'flex'; });
        closeBtn?.addEventListener('click', closeModal);
        cancelBtn?.addEventListener('click', closeModal);

        form?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const iptalId = (document.getElementById('iptalId') as HTMLInputElement)?.value;
            const data = {
                police_id: (document.getElementById('iptalPoliceId') as HTMLSelectElement)?.value,
                iptal_tarihi: (document.getElementById('iptalTarihi') as HTMLInputElement)?.value,
                iade_tutari: (document.getElementById('iadeTutari') as HTMLInputElement)?.value || null,
                durum: (document.getElementById('iptalDurum') as HTMLSelectElement)?.value,
                iptal_nedeni: (document.getElementById('iptalNedeni') as HTMLTextAreaElement)?.value,
                notlar: (document.getElementById('iptalNotlar') as HTMLTextAreaElement)?.value
            };
            const errorDiv = document.getElementById('iptalFormError');
            try {
                if (iptalId) {
                    await iptallerApi.update(parseInt(iptalId), data);
                } else {
                    await iptallerApi.create(data);
                }
                closeModal();
                this.loadIptaller();
            } catch (error: any) {
                if (errorDiv) { errorDiv.textContent = error.message; errorDiv.style.display = 'block'; }
            }
        });

        document.getElementById('iptallerList')?.addEventListener('click', async (e) => {
            const btn = (e.target as HTMLElement).closest('button');
            if (!btn) return;
            const id = btn.getAttribute('data-id');
            if (!id) return;
            if (btn.classList.contains('delete-btn')) {
                if (confirm('Bu iptal kaydını silmek istediğinize emin misiniz?')) {
                    await iptallerApi.delete(parseInt(id));
                    this.loadIptaller();
                }
            }
        });
    }

    private async loadRuhsatlarForSelect(selectId: string) {
        const select = document.getElementById(selectId) as HTMLSelectElement;
        if (!select) return;
        try {
            const ruhsatlar = await ruhsatlarApi.getAll();
            select.innerHTML = '<option value="">Araç Seçin</option>' +
                ruhsatlar.map((r: any) => `<option value="${r.id}">${r.plaka || 'Plakasız'} - ${r.marka || ''} ${r.model || ''}</option>`).join('');
        } catch (error) { console.error('Ruhsatlar load error:', error); }
    }

    private async loadPolicelerForSelect(selectId: string) {
        const select = document.getElementById(selectId) as HTMLSelectElement;
        if (!select) return;
        try {
            const policeler = await policelerApi.getAll();
            select.innerHTML = '<option value="">Poliçe Seçin</option>' +
                policeler.map((p: any) => `<option value="${p.id}">${p.police_no || 'No yok'} - ${p.sigorta_tipi} (${p.kullanici_adi || p.plaka || '-'})</option>`).join('');
        } catch (error) { console.error('Policeler load error:', error); }
    }

    private async loadMuayeneler() {
        const container = document.getElementById('muayenelerList');
        if (!container) return;
        try {
            const muayeneler = await muayenelerApi.getAll();
            const today = new Date();
            container.innerHTML = `
                <table class="data-table">
                    <thead><tr>
                        <th>Plaka</th><th>Araç</th><th>Müşteri</th><th>Telefon</th>
                        <th>Son Muayene</th><th>Sonraki Muayene</th><th>Kalan Gün</th><th>Sonuç</th><th>İşlemler</th>
                    </tr></thead>
                    <tbody>
                        ${muayeneler.map((m: any) => {
                            const next = new Date(m.sonraki_muayene);
                            const diffDays = Math.ceil((next.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                            const durumClass = diffDays < 0 ? 'badge-iptal' : diffDays <= 30 ? 'badge-islemde' : 'badge-aktif';
                            const durumText = diffDays < 0 ? 'Gecikmiş' : diffDays <= 30 ? 'Yaklaşıyor' : 'Geçerli';
                            return `<tr>
                                <td><strong>${m.plaka || '-'}</strong></td>
                                <td>${m.marka || ''} ${m.model || ''}</td>
                                <td>${m.kullanici_adi || '-'}</td>
                                <td>${m.kullanici_telefon || '-'}</td>
                                <td>${m.muayene_tarihi || '-'}</td>
                                <td>${m.sonraki_muayene || '-'}</td>
                                <td><span class="badge ${durumClass}">${diffDays} gün (${durumText})</span></td>
                                <td><span class="badge badge-${m.sonuc}">${m.sonuc || '-'}</span></td>
                                <td class="actions"><button class="btn-sm btn-danger delete-btn" data-id="${m.id}">Sil</button></td>
                            </tr>`;
                        }).join('')}
                    </tbody>
                </table>
                ${muayeneler.length === 0 ? '<p class="empty-state">Henüz muayene kaydı yok.</p>' : ''}
            `;
        } catch (error) { container.innerHTML = '<p class="error">Muayeneler yüklenirken hata oluştu.</p>'; }
    }

    private async loadBelgeler() {
        const container = document.getElementById('belgelerList');
        if (!container) return;
        try {
            const belgeler = await belgelerApi.getAll();
            container.innerHTML = `
                <table class="data-table">
                    <thead><tr>
                        <th>Dosya</th><th>Tip</th><th>Müşteri</th><th>Poliçe</th><th>Açıklama</th><th>Tarih</th><th>İşlemler</th>
                    </tr></thead>
                    <tbody>
                        ${belgeler.map((b: any) => `
                            <tr>
                                <td><a href="${API_URL.replace('/api', '')}${b.dosya_yolu}" target="_blank">${b.dosya_adi}</a></td>
                                <td><span class="badge">${b.belge_tipi}</span></td>
                                <td>${b.kullanici_adi || '-'}</td>
                                <td>${b.police_no || '-'}</td>
                                <td>${b.aciklama || '-'}</td>
                                <td>${b.created_at ? new Date(b.created_at).toLocaleDateString('tr-TR') : '-'}</td>
                                <td class="actions"><button class="btn-sm btn-danger delete-btn" data-id="${b.id}">Sil</button></td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
                ${belgeler.length === 0 ? '<p class="empty-state">Henüz belge yok.</p>' : ''}
            `;
        } catch (error) { container.innerHTML = '<p class="error">Belgeler yüklenirken hata oluştu.</p>'; }
    }

    private async loadIptaller() {
        const container = document.getElementById('iptallerList');
        if (!container) return;
        try {
            const iptaller = await iptallerApi.getAll();
            container.innerHTML = `
                <table class="data-table">
                    <thead><tr>
                        <th>Poliçe No</th><th>Tip</th><th>Müşteri</th><th>Plaka</th>
                        <th>İptal Tarihi</th><th>İade</th><th>Durum</th><th>İşlemler</th>
                    </tr></thead>
                    <tbody>
                        ${iptaller.map((i: any) => `
                            <tr>
                                <td>${i.police_no || '-'}</td>
                                <td><span class="badge">${i.sigorta_tipi || '-'}</span></td>
                                <td>${i.kullanici_adi || '-'}</td>
                                <td>${i.plaka || '-'}</td>
                                <td>${i.iptal_tarihi || '-'}</td>
                                <td>${i.iade_tutari ? i.iade_tutari + ' TL' : '-'}</td>
                                <td><span class="badge badge-${i.durum}">${i.durum}</span></td>
                                <td class="actions"><button class="btn-sm btn-danger delete-btn" data-id="${i.id}">Sil</button></td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
                ${iptaller.length === 0 ? '<p class="empty-state">Henüz iptal kaydı yok.</p>' : ''}
            `;
        } catch (error) { container.innerHTML = '<p class="error">İptaller yüklenirken hata oluştu.</p>'; }
    }

    private async loadAdminStats() {
        try {
            const stats = await adminStatsApi.get();
            const set = (id: string, val: any) => { const el = document.getElementById(id); if (el) el.textContent = val; };
            set('statMusteri', stats.toplam_musteri);
            set('statPolice', stats.aktif_police);
            set('statBitis', stats.yaklasan_bitis);
            set('statMuayene', stats.yaklasan_muayene);
            set('statTeklif', stats.bekleyen_teklif);
            set('statBelge', stats.toplam_belge);
        } catch (error) { console.error('Stats load error:', error); }
    }

    private async loadPageData() {
        const path = window.location.pathname;

        if (path === '/admin/kullanicilar') {
            await this.loadUsers();
        } else if (path === '/admin/ruhsatlar') {
            await this.loadUsersForSelect('ruhsatUserId');
            await this.loadRuhsatlar();
        } else if (path === '/admin/policeler') {
            await this.loadUsersForSelect('policeUserId');
            await this.loadPoliceler();
        } else if (path === '/admin/teklifler') {
            await this.loadTeklifler();
        } else if (path === '/admin/muayeneler') {
            await this.loadRuhsatlarForSelect('muayeneRuhsatId');
            await this.loadMuayeneler();
        } else if (path === '/admin/belgeler') {
            await this.loadUsersForSelect('belgeUserId');
            await this.loadPolicelerForSelect('belgePoliceId');
            await this.loadBelgeler();
        } else if (path === '/admin/iptaller') {
            await this.loadPolicelerForSelect('iptalPoliceId');
            await this.loadIptaller();
        } else if (path === '/admin') {
            await this.loadAdminStats();
        } else if (path === '/ruhsatlarim') {
            await this.loadMyRuhsatlar();
        } else if (path === '/policelerim') {
            await this.loadMyPoliceler();
        }
    }

    private async loadUsers() {
        const container = document.getElementById('usersList');
        if (!container) return;

        try {
            const users = await usersApi.getAll();
            container.innerHTML = `
                <table class="data-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Ad Soyad</th>
                            <th>Email</th>
                            <th>Telefon</th>
                            <th>Rol</th>
                            <th>İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${users.map((u: any) => `
                            <tr>
                                <td>${u.id}</td>
                                <td>${u.ad} ${u.soyad}</td>
                                <td>${u.email}</td>
                                <td>${u.telefon || '-'}</td>
                                <td><span class="badge badge-${u.rol}">${u.rol}</span></td>
                                <td class="actions">
                                    <button class="btn-sm edit-btn" data-id="${u.id}">Düzenle</button>
                                    <button class="btn-sm btn-info add-ruhsat-btn" data-id="${u.id}" data-name="${u.ad} ${u.soyad}">+ Ruhsat</button>
                                    <button class="btn-sm btn-info add-police-btn" data-id="${u.id}" data-name="${u.ad} ${u.soyad}">+ Poliçe</button>
                                    <button class="btn-sm btn-info add-belge-btn" data-id="${u.id}" data-name="${u.ad} ${u.soyad}">+ Belge</button>
                                    <button class="btn-sm btn-danger delete-btn" data-id="${u.id}">Sil</button>
                                </td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            `;
        } catch (error) {
            container.innerHTML = '<p class="error">Kullanıcılar yüklenirken hata oluştu.</p>';
        }
    }

    private async loadUsersForSelect(selectId: string) {
        const select = document.getElementById(selectId) as HTMLSelectElement;
        if (!select) return;

        try {
            const users = await usersApi.getAll();
            select.innerHTML = '<option value="">Kullanıcı Seçin</option>' +
                users.map((u: any) => `<option value="${u.id}">${u.ad} ${u.soyad} (${u.email})</option>`).join('');
        } catch (error) {
            console.error('Users load error:', error);
        }
    }

    private async loadRuhsatlar() {
        const container = document.getElementById('ruhsatlarList');
        if (!container) return;

        try {
            const ruhsatlar = await ruhsatlarApi.getAll();
            const ruhsatMap = new Map<string, any>();
            ruhsatlar.forEach((r: any) => ruhsatMap.set(String(r.id), r));

            container.innerHTML = `
                <div class="ruhsat-list">
                    ${ruhsatlar.map((r: any) => `
                        <div class="ruhsat-row" data-id="${r.id}">
                            <div class="ruhsat-row-main">
                                <span class="ruhsat-plaka">${r.plaka || 'Plaka Yok'}</span>
                                <span class="ruhsat-arac">${r.marka || ''} ${r.model || ''} ${r.model_yili ? '(' + r.model_yili + ')' : ''}</span>
                                <span class="ruhsat-owner">${r.ruhsat_sahibi || '-'}</span>
                                <span class="ruhsat-user badge">${r.kullanici_adi || '-'}</span>
                            </div>
                            <div class="ruhsat-row-sub">
                                <button class="btn-sm btn-danger delete-btn" data-id="${r.id}" onclick="event.stopPropagation()">Sil</button>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="M9 18l6-6-6-6"/></svg>
                            </div>
                        </div>
                    `).join('')}
                </div>

                <div id="adminRuhsatDetailModal" class="modal" style="display:none;">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h2 id="adminRuhsatDetailTitle">Ruhsat Detay</h2>
                            <button class="modal-close" id="closeAdminRuhsatDetail">&times;</button>
                        </div>
                        <div id="adminRuhsatDetailBody" class="modal-body"></div>
                    </div>
                </div>
            `;

            // Satır tıklama → detay popup
            container.querySelectorAll('.ruhsat-row').forEach(row => {
                row.addEventListener('click', () => {
                    const id = row.getAttribute('data-id');
                    const r = ruhsatMap.get(id || '');
                    if (!r) return;
                    const modal = document.getElementById('adminRuhsatDetailModal');
                    const title = document.getElementById('adminRuhsatDetailTitle');
                    const body = document.getElementById('adminRuhsatDetailBody');
                    if (title) title.textContent = r.plaka || 'Ruhsat Detay';
                    if (body) body.innerHTML = `
                        ${r.ruhsat_image_path ? `<img src="${r.ruhsat_image_path}" style="width:100%;border-radius:8px;margin-bottom:16px;" alt="Ruhsat">` : ''}
                        <div class="card-details">
                            <div class="detail-row"><span class="detail-label">Plaka</span><span class="detail-value">${r.plaka || '-'}</span></div>
                            <div class="detail-row"><span class="detail-label">Araç</span><span class="detail-value">${r.marka || '-'} ${r.model || ''} ${r.model_yili || ''}</span></div>
                            <div class="detail-row"><span class="detail-label">Ruhsat Sahibi</span><span class="detail-value">${r.ruhsat_sahibi || '-'}</span></div>
                            <div class="detail-row"><span class="detail-label">Kullanıcı</span><span class="detail-value">${r.kullanici_adi || '-'}</span></div>
                            <div class="detail-row"><span class="detail-label">TC No</span><span class="detail-value">${r.tc_no || '-'}</span></div>
                            <div class="detail-row"><span class="detail-label">Şasi No</span><span class="detail-value">${r.sasi_no || '-'}</span></div>
                            <div class="detail-row"><span class="detail-label">Motor No</span><span class="detail-value">${r.motor_no || '-'}</span></div>
                            ${r.belge_seri_no ? `<div class="detail-row"><span class="detail-label">Belge Seri No</span><span class="detail-value">${r.belge_seri_no}</span></div>` : ''}
                            ${r.muayene_tarihi ? `<div class="detail-row"><span class="detail-label">Muayene Tarihi</span><span class="detail-value">${r.muayene_tarihi}</span></div>` : ''}
                            ${r.muayene_bitis ? `<div class="detail-row"><span class="detail-label">Muayene Bitiş</span><span class="detail-value">${r.muayene_bitis}</span></div>` : ''}
                        </div>
                    `;
                    if (modal) modal.style.display = 'flex';
                });
            });

            document.getElementById('closeAdminRuhsatDetail')?.addEventListener('click', () => {
                const modal = document.getElementById('adminRuhsatDetailModal');
                if (modal) modal.style.display = 'none';
            });
        } catch (error) {
            container.innerHTML = '<p class="error">Ruhsatlar yüklenirken hata oluştu.</p>';
        }
    }

    private async loadPoliceler() {
        const container = document.getElementById('policelerList');
        if (!container) return;

        try {
            let policeler = await policelerApi.getAll();

            // Apply filters
            const filterTip = (document.getElementById('filterTip') as HTMLSelectElement)?.value;
            const filterDurum = (document.getElementById('filterDurum') as HTMLSelectElement)?.value;

            if (filterTip) policeler = policeler.filter((p: any) => p.sigorta_tipi === filterTip);
            if (filterDurum) policeler = policeler.filter((p: any) => p.durum === filterDurum);

            container.innerHTML = `
                <table class="data-table">
                    <thead>
                        <tr>
                            <th>Poliçe No</th>
                            <th>Tip</th>
                            <th>Kullanıcı</th>
                            <th>Araç</th>
                            <th>Başlangıç</th>
                            <th>Bitiş</th>
                            <th>Durum</th>
                            <th>İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${policeler.map((p: any) => `
                            <tr>
                                <td>${p.police_no || '-'}</td>
                                <td><span class="badge">${p.sigorta_tipi}</span></td>
                                <td>${p.kullanici_adi || '-'}</td>
                                <td>${p.plaka || '-'}</td>
                                <td>${p.baslangic_tarihi || '-'}</td>
                                <td>${p.bitis_tarihi || '-'}</td>
                                <td><span class="badge badge-${p.durum}">${p.durum}</span></td>
                                <td class="actions">
                                    <button class="btn-sm btn-danger delete-btn" data-id="${p.id}">Sil</button>
                                </td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            `;
        } catch (error) {
            container.innerHTML = '<p class="error">Poliçeler yüklenirken hata oluştu.</p>';
        }
    }

    private async loadMyRuhsatlar() {
        const container = document.getElementById('ruhsatlarList');
        if (!container) return;

        try {
            const ruhsatlar = await ruhsatlarApi.getAll();
            if (ruhsatlar.length === 0) {
                container.innerHTML = '<p class="empty-state">Henüz kayıtlı ruhsatınız bulunmuyor.</p>';
                return;
            }
            // Store ruhsat data in a map for click lookup
            const ruhsatMap = new Map<string, any>();
            ruhsatlar.forEach((r: any) => ruhsatMap.set(String(r.id), r));

            container.innerHTML = `
                <div class="ruhsat-list">
                    ${ruhsatlar.map((r: any) => `
                        <div class="ruhsat-row" data-id="${r.id}">
                            <div class="ruhsat-row-main">
                                <span class="ruhsat-plaka">${r.plaka || 'Plaka Yok'}</span>
                                <span class="ruhsat-arac">${r.marka || ''} ${r.model || ''} ${r.model_yili ? '(' + r.model_yili + ')' : ''}</span>
                            </div>
                            <div class="ruhsat-row-sub">
                                <span>${r.ruhsat_sahibi || '-'}</span>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="M9 18l6-6-6-6"/></svg>
                            </div>
                        </div>
                    `).join('')}
                </div>

                <div id="ruhsatDetailModal" class="modal" style="display:none;">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h2 id="ruhsatDetailTitle">Ruhsat Detay</h2>
                            <button class="modal-close" id="closeRuhsatDetail">&times;</button>
                        </div>
                        <div id="ruhsatDetailBody" class="modal-body"></div>
                    </div>
                </div>
            `;

            // Satır tıklama → modal aç
            container.querySelectorAll('.ruhsat-row').forEach(row => {
                row.addEventListener('click', () => {
                    const id = row.getAttribute('data-id');
                    const r = ruhsatMap.get(id || '');
                    if (!r) return;
                    const modal = document.getElementById('ruhsatDetailModal');
                    const title = document.getElementById('ruhsatDetailTitle');
                    const body = document.getElementById('ruhsatDetailBody');
                    if (title) title.textContent = r.plaka || 'Ruhsat Detay';
                    if (body) body.innerHTML = `
                        ${r.ruhsat_image_path ? `<img src="${r.ruhsat_image_path}" style="width:100%;border-radius:8px;margin-bottom:16px;" alt="Ruhsat">` : ''}
                        <div class="card-details">
                            <div class="detail-row"><span class="detail-label">Araç</span><span class="detail-value">${r.marka || '-'} ${r.model || ''}</span></div>
                            <div class="detail-row"><span class="detail-label">Model Yılı</span><span class="detail-value">${r.model_yili || '-'}</span></div>
                            <div class="detail-row"><span class="detail-label">Ruhsat Sahibi</span><span class="detail-value">${r.ruhsat_sahibi || '-'}</span></div>
                            <div class="detail-row"><span class="detail-label">TC No</span><span class="detail-value">${r.tc_no || '-'}</span></div>
                            <div class="detail-row"><span class="detail-label">Şasi No</span><span class="detail-value">${r.sasi_no || '-'}</span></div>
                            <div class="detail-row"><span class="detail-label">Motor No</span><span class="detail-value">${r.motor_no || '-'}</span></div>
                            ${r.belge_seri_no ? `<div class="detail-row"><span class="detail-label">Belge Seri No</span><span class="detail-value">${r.belge_seri_no}</span></div>` : ''}
                            ${r.muayene_bitis ? `<div class="detail-row"><span class="detail-label">Muayene Bitiş</span><span class="detail-value">${r.muayene_bitis}</span></div>` : ''}
                        </div>
                    `;
                    if (modal) modal.style.display = 'flex';
                });
            });

            document.getElementById('closeRuhsatDetail')?.addEventListener('click', () => {
                const modal = document.getElementById('ruhsatDetailModal');
                if (modal) modal.style.display = 'none';
            });
        } catch (error) {
            container.innerHTML = '<p class="error">Ruhsatlar yüklenirken hata oluştu.</p>';
        }
    }

    private async loadMyPoliceler() {
        const container = document.getElementById('policelerList');
        if (!container) return;

        try {
            const policeler = await policelerApi.getAll();
            if (policeler.length === 0) {
                container.innerHTML = '<p class="empty-state">Henüz kayıtlı poliçeniz bulunmuyor.</p>';
                return;
            }
            container.innerHTML = `
                <div class="cards-grid">
                    ${policeler.map((p: any) => `
                        <div class="data-card">
                            <span class="badge badge-${p.durum}">${p.durum}</span>
                            <h3>${p.sigorta_tipi.toUpperCase()}</h3>
                            <p><strong>Poliçe No:</strong> ${p.police_no || '-'}</p>
                            <p><strong>Araç:</strong> ${p.plaka || '-'}</p>
                            <p><strong>Başlangıç:</strong> ${p.baslangic_tarihi || '-'}</p>
                            <p><strong>Bitiş:</strong> ${p.bitis_tarihi || '-'}</p>
                            <p><strong>Prim:</strong> ${p.prim_tutari ? p.prim_tutari + ' TL' : '-'}</p>
                        </div>
                    `).join('')}
                </div>
            `;
        } catch (error) {
            container.innerHTML = '<p class="error">Poliçeler yüklenirken hata oluştu.</p>';
        }
    }

    updateAuthUI() {
        const user = authApi.getCurrentUser();
        const loginBtn = document.getElementById('loginBtn');
        const userMenu = document.getElementById('userMenu');
        const userName = document.getElementById('userName');
        const adminLink = document.getElementById('adminLink');
        const adminDivider = document.getElementById('adminDivider');

        if (user) {
            // User is logged in
            if (loginBtn) loginBtn.style.display = 'none';
            if (userMenu) userMenu.style.display = 'block';
            if (userName) userName.textContent = user.ad;

            // Show admin link if admin
            if (user.rol === 'admin') {
                if (adminLink) adminLink.style.display = 'block';
                if (adminDivider) adminDivider.style.display = 'block';
            } else {
                if (adminLink) adminLink.style.display = 'none';
                if (adminDivider) adminDivider.style.display = 'none';
            }
        } else {
            // User is not logged in
            if (loginBtn) loginBtn.style.display = 'flex';
            if (userMenu) userMenu.style.display = 'none';
        }
    }

    init() {
        document.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            const link = target.closest('a');

            if (link && link.href.startsWith(window.location.origin)) {
                const pathname = new URL(link.href).pathname;
                // Skip: file downloads, uploads, target="_blank" links
                if (link.getAttribute('target') === '_blank' ||
                    pathname.startsWith('/uploads/') ||
                    pathname.match(/\.\w{2,5}$/)) {
                    return; // Let browser handle normally
                }
                e.preventDefault();
                this.navigate(pathname);
            }
        });

        this.updateAuthUI();
        this.handleRoute();
    }
}
